import React, { useEffect, useMemo, useState } from 'react';
import {
  Download,
  FileText,
  Loader2,
  Printer,
  X,
} from 'lucide-react';

import {
  feesApi,
  getFeesApiErrorMessage,
} from '../services/feesApi';

const money = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const FeeReceiptModal = ({
  open,
  onClose,
  schoolId,
  receiptId,
  initialReceipt = null,
}) => {
  const [receipt, setReceipt] = useState(initialReceipt);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');

  useEffect(() => {
    setReceipt(initialReceipt);
  }, [initialReceipt]);

  useEffect(() => {
    if (!open || !schoolId || !receiptId) return undefined;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const data = await feesApi.getReceipt(schoolId, receiptId);
        if (!cancelled) setReceipt(data);
      } catch (requestError) {
        if (!cancelled) {
          setError(
            getFeesApiErrorMessage(
              requestError,
              'Unable to load the fee receipt.',
            ),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [open, schoolId, receiptId]);

  useEffect(
    () => () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    },
    [pdfUrl],
  );

  const payment = receipt?.payment;
  const allocations = useMemo(
    () => payment?.allocations || [],
    [payment],
  );

  const loadPdfUrl = async () => {
    if (pdfUrl) return pdfUrl;

    setPdfLoading(true);
    try {
      const blob = await feesApi.getReceiptPdfBlob(schoolId, receiptId);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      return url;
    } catch (requestError) {
      setError(
        getFeesApiErrorMessage(
          requestError,
          'Unable to generate the receipt PDF.',
        ),
      );
      return '';
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownload = async () => {
    const url = await loadPdfUrl();
    if (!url) return;

    const link = document.createElement('a');
    link.href = url;
    link.download = `${receipt?.receipt_no || 'fee-receipt'}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handlePrint = async () => {
    const url = await loadPdfUrl();
    if (!url) return;

    const printWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (printWindow) {
      printWindow.addEventListener('load', () => printWindow.print());
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Fee Receipt</h2>
            <p className="mt-1 text-xs text-gray-500">
              {receipt?.receipt_no || 'Receipt preview'}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-6">
          {loading ? (
            <div className="flex min-h-56 flex-col items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="mt-3 text-sm text-gray-500">Loading receipt...</p>
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          ) : (
            <div className="space-y-6">
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="rounded-xl bg-blue-100 p-3 text-blue-600">
                      <FileText className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Receipt number</p>
                      <p className="font-semibold text-gray-900">
                        {receipt?.receipt_no || '—'}
                      </p>
                    </div>
                  </div>

                  <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                    {receipt?.status || 'ISSUED'}
                  </span>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <p className="text-xs text-gray-500">Student</p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      {receipt?.student_name || payment?.student_name || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Enrolment</p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      {receipt?.enrolment_no || payment?.enrolment_no || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Academic year</p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      {receipt?.academic_year_name || payment?.academic_year_name || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Payment date</p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      {formatDate(payment?.payment_date || receipt?.receipt_date)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full min-w-[850px] text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-4 py-3 text-left">Fee head</th>
                      <th className="px-4 py-3 text-right">Scheduled</th>
                      <th className="px-4 py-3 text-right">Discount</th>
                      <th className="px-4 py-3 text-right">Fine</th>
                      <th className="px-4 py-3 text-right">Paid</th>
                      <th className="px-4 py-3 text-right">Balance after</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allocations.map((allocation) => (
                      <React.Fragment key={allocation.id}>
                        <tr className="border-t border-gray-100">
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">
                              {allocation.fee_head_name_snapshot || allocation.ledger_name}
                            </p>
                            <p className="mt-1 text-xs text-gray-500">
                              {allocation.category_name_snapshot || '—'}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {money(allocation.scheduled_amount_snapshot)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {money(allocation.discount_amount)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {money(allocation.fine_amount)}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-900">
                            {money(allocation.amount)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {money(allocation.balance_after)}
                          </td>
                        </tr>
                        {(allocation.discount_reason || allocation.fine_reason) && (
                          <tr className="bg-gray-50/60">
                            <td colSpan={6} className="px-4 py-2 text-xs text-gray-600">
                              {allocation.discount_reason && (
                                <span className="mr-4">
                                  Discount reason: {allocation.discount_reason}
                                </span>
                              )}
                              {allocation.fine_reason && (
                                <span>Fine reason: {allocation.fine_reason}</span>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot className="border-t border-gray-200 bg-gray-50">
                    <tr>
                      <td colSpan={4} className="px-4 py-4 font-semibold text-gray-900">
                        Total paid
                      </td>
                      <td className="px-4 py-4 text-right text-lg font-bold text-blue-600">
                        {money(payment?.amount_paid)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-500">Payment mode</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    {payment?.payment_mode || '—'}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-500">Reference number</p>
                  <p className="mt-1 break-all text-sm font-semibold text-gray-900">
                    {payment?.reference_number || '—'}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-500">Notes</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    {payment?.notes || '—'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handlePrint}
            disabled={pdfLoading || !receiptId}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
            Print
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={pdfLoading || !receiptId}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeeReceiptModal;
